import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeftIcon, CheckIcon, EyeIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { toast } from "../../../utils/toast";
import { Button, Card, ConfirmModal, Container, EmptyState, Loading, PageHeader, SectionTitle, Select, Tag, Textarea } from "../../ui";
import { errorMessageOf, fieldErrorsOf, problemMessage, problemOf, scholarshipsApi } from "../../../api/scholarships";
import { countryName, currencyOptionsOf, formatDuration, formatKigaliDate, kindOf, useOptions } from "../model";
import { COURSE_SECTORS, formatDateTime, openDocument } from "../review/model";
import { AdmissionFields, DeclarationCard, EmploymentFields, FundingFields, PreviewModal, RequirementRow, answerRowsOf } from "./ApplicationSections";
import { LoanCard, TravelCard } from "./LoanCards";
import MessageThread from "../loans/MessageThread";
import { AMOUNTS, amountOf } from "../funding";
import EligibilityNote, { PROFILE_PATH } from "./EligibilityNote";
import { StudentDocumentRequests } from "../DocumentRequests";
import { STUDENT_KEY, closesIn, myApplicationKey, studentStatusOf, useAcademicProfile, useConsentStatement, useMyApplication, useStudentScholarship } from "./model";

const text = (value) => (value == null ? "" : String(value));

// The form as inputs hold it (strings): the student's studies, then for loans the sector and the
// funding requested, and for a loan where the student was admitted (abroad, with the country)
// Loans follow the student's own admission, anywhere abroad
const asksAdmission = (kind) => Boolean(kind.studentChoosesPlace);

const formOf = (form) => {
  const funding = form.funding ?? {};
  return {
    admittedInstitution: form.admittedInstitution ?? "",
    admittedProgramme: form.admittedProgramme ?? "",
    studyCountryCode: form.studyCountryCode ?? "",
    courseSector: form.courseSector ?? "",
    employed: form.employed == null ? "" : form.employed ? "yes" : "no",
    employer: form.employer ?? "",
    currency: funding.currency ?? "",
    ...Object.fromEntries(AMOUNTS.map(({ key }) => [key, text(funding[key])])),
    studyStartDate: funding.studyStartDate ?? "",
    studyEndDate: funding.studyEndDate ?? "",
    started: funding.coveredPeriodMonths != null || funding.remainingPeriodMonths != null ? "yes" : "no",
    coveredPeriodMonths: text(funding.coveredPeriodMonths),
    remainingPeriodMonths: text(funding.remainingPeriodMonths),
  };
};
const bodyOf = (form, kind) => ({
  employed: form.employed === "" ? null : form.employed === "yes",
  employer: form.employed === "yes" ? form.employer.trim() || null : null,
  ...(asksAdmission(kind) && {
    admittedInstitution: form.admittedInstitution.trim() || null,
    admittedProgramme: form.admittedProgramme.trim() || null,
  }),
  ...(kind.studentChoosesPlace && { studyCountryCode: form.studyCountryCode || null }),
  ...(kind.isLoan && {
    courseSector: form.courseSector || null,
    funding: {
      currency: form.currency || null,
      ...Object.fromEntries(AMOUNTS.map(({ key }) => [key, amountOf(form[key])])),
      studyStartDate: form.studyStartDate || null,
      studyEndDate: form.studyEndDate || null,
      coveredPeriodMonths: form.started === "yes" ? amountOf(form.coveredPeriodMonths) : null,
      remainingPeriodMonths: form.started === "yes" ? amountOf(form.remainingPeriodMonths) : null,
    },
  }),
});

/**
 * The student's studies: from their academic profile while the application is a draft (copied onto
 * it when they submit), then as they were sent. A draft also says whether the profile meets who may apply.
 */
function Studies({ application }) {
  const draft = application.editable;
  const { data: profile, isPending } = useAcademicProfile();
  if (draft && isPending) return <Loading label="Loading your academic profile…" />;
  // Both the profile and the copy sent on submit name their level and field
  const shown = draft ? profile : application.profile;
  const studies = shown && {
    level: shown.currentLevel?.name,
    field: shown.fieldOfStudy?.name,
    institution: shown.institutionName,
    programme: shown.programmeName,
    grade: shown.gradePercent,
    year: shown.completionYear,
  };
  const rows = studies && [
    ["Current level of study", studies.level],
    ["Field of study", studies.field],
    ["School or university", studies.institution],
    ["Programme or subject combination", studies.programme],
    ["Final grade", studies.grade == null ? "—" : `${Number(studies.grade)}%`],
    ["Year completed", studies.year],
  ];
  return (
    <div className="md:col-span-2 flex flex-col gap-3">
      {rows ? (
        <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          {rows.map(([label, value]) => (
            <div key={label}>
              <dt className="text-slate-500">{label}</dt>
              <dd className="font-medium text-slate-800">{value || "—"}</dd>
            </div>
          ))}
        </dl>
      ) : (
        !draft && <p className="text-sm text-slate-500">No studies were sent with this application.</p>
      )}
      {draft && (
        <>
          <EligibilityNote eligibility={application.eligibility} />
          {profile && (
            <p className="text-xs text-slate-500">
              From your{" "}
              <Link to={PROFILE_PATH} className="font-medium text-brand hover:underline">
                academic profile
              </Link>
              ; changes there apply to this application until you submit it.
            </p>
          )}
        </>
      )}
    </div>
  );
}

// What the student is about to send, as HEC will read it: shown before they submit
function ApplicationPreview({ application, kind, form, requirements, amounts, submitting, onBack, onSubmit }) {
  return (
    <PreviewModal
      subtitle={`${application.scholarship.name} · ${application.programme.name}`}
      studies={<Studies application={application} />}
      answers={answerRowsOf(form, { admission: asksAdmission(kind), loan: kind.isLoan, amounts })}
      requirements={requirements}
      hasDocument={(r) => application.documents.some((d) => d.requirementId === r.id)}
      closesAt={application.scholarship.closesAt}
      submitting={submitting}
      onBack={onBack}
      onSubmit={onSubmit}
    />
  );
}

/**
 * Journey steps 6 and 7: a student's application. While it is a draft they fill in the form,
 * upload the documents their programme needs, agree to the declaration, check a preview and
 * submit, or cancel it. Once sent they may take it back to change it until the deadline, and
 * withdraw it until the outcome is out. Its progress is on the applications list.
 */
export default function ApplicationPage() {
  const { applicationId } = useParams();
  const queryClient = useQueryClient();
  const { data, isPending, isError, refetch } = useMyApplication(applicationId);
  const application = data?.data;
  const { data: scholarship } = useStudentScholarship(application?.scholarship.id);
  const { data: consent } = useConsentStatement();
  const { data: options } = useOptions();

  const [form, setForm] = useState(null);
  const [savingForm, setSavingForm] = useState(false);
  const [uploading, setUploading] = useState(null); // requirement ID
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [problems, setProblems] = useState({}); // field → message, from a refused submit
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reopening, setReopening] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (application && form === null) setForm(formOf(application.form));
  }, [application, form]);

  if (isPending || (application && form === null)) return <Loading label="Loading your application…" />;
  if (isError) return <EmptyState title="Could not load this application" onRefresh={refetch} />;

  const kind = kindOf(application.scholarship.type);
  const status = studentStatusOf(application.studentStatus);
  const editable = application.editable;
  const programme = scholarship?.programmes.find((p) => p.id === application.programme.id);
  const requirements = programme?.requirements ?? [];
  const etag = () => queryClient.getQueryData(myApplicationKey(applicationId))?.etag ?? `"${application.version}"`;
  const loan = kind.isLoan;
  // Only abroad loans pay an air ticket
  const amounts = AMOUNTS.filter(({ key }) => kind.travels || key !== "airTicketAllowance");
  const formChanged = JSON.stringify(bodyOf(form, kind)) !== JSON.stringify(bodyOf(formOf(application.form), kind));
  const reload = () => queryClient.invalidateQueries({ queryKey: myApplicationKey(applicationId) });
  const refreshLists = () => queryClient.invalidateQueries({ queryKey: STUDENT_KEY });

  const saveForm = async ({ quiet = false } = {}) => {
    setSavingForm(true);
    try {
      const result = await scholarshipsApi.patch(`student/applications/${applicationId}`, bodyOf(form, kind), { ifMatch: etag() });
      queryClient.setQueryData(myApplicationKey(applicationId), result);
      if (!quiet) toast.success("Saved");
      return true;
    } catch (error) {
      console.error(`Failed to save application ${applicationId}:`, error);
      setProblems((prev) => ({ ...prev, ...fieldErrorsOf(problemOf(error)) }));
      toast.error(errorMessageOf(error, "Failed to save. Please try again."));
      if (problemOf(error)?.code === "stale-version") reload();
      return false;
    } finally {
      setSavingForm(false);
    }
  };

  const upload = async (requirement, file) => {
    setUploading(requirement.id);
    setProblems((prev) => ({ ...prev, [`requirement:${requirement.id}`]: undefined }));
    try {
      const body = new FormData();
      body.append("requirementId", requirement.id);
      body.append("file", file);
      await scholarshipsApi.post(`student/applications/${applicationId}/documents`, body);
      toast.success(`${requirement.label} uploaded`);
    } catch (error) {
      console.error(`Failed to upload ${requirement.code} to application ${applicationId}:`, error);
      const message = errorMessageOf(error, "Upload failed. Please try again.");
      setProblems((prev) => ({ ...prev, [`requirement:${requirement.id}`]: message }));
      toast.error(message);
    } finally {
      setUploading(null);
      reload();
    }
  };

  const removeDocument = async (document) => {
    try {
      await scholarshipsApi.delete(`student/applications/${applicationId}/documents/${document.id}`);
      toast.success("Document removed");
    } catch (error) {
      console.error(`Failed to remove document ${document.id}:`, error);
      toast.error(errorMessageOf(error, "Failed to remove the document. Please try again."));
    } finally {
      reload();
    }
  };

  const submit = async () => {
    setProblems({});
    if (formChanged && !(await saveForm({ quiet: true }))) return;
    setSubmitting(true);
    try {
      const result = await scholarshipsApi.post(`student/applications/${applicationId}/submit`, { consentVersion: consent.version, consentGiven: agreed }, { ifMatch: etag() });
      queryClient.setQueryData(myApplicationKey(applicationId), result);
      refreshLists();
      toast.success("Application submitted. Good luck!");
      setAgreed(false);
    } catch (error) {
      const problem = problemOf(error);
      console.error(`Failed to submit application ${applicationId}:`, error);
      if (problem?.code === "submission-invalid") {
        setProblems(Object.fromEntries(problem.errors.map((e) => [e.field ?? e.code, problemMessage(e.code) ?? e.code])));
        toast.error("Your application is not complete yet. See the highlighted parts.");
      } else {
        toast.error(errorMessageOf(error, "Failed to submit. Please try again."));
      }
      if (problem?.code === "stale-version") reload();
    } finally {
      setSubmitting(false);
      setPreviewing(false);
    }
  };

  // The preview shows what is saved, so pending answers are saved first
  const preview = async () => {
    setProblems({});
    if (formChanged && !(await saveForm({ quiet: true }))) return;
    setPreviewing(true);
  };

  // An unsent application is cancelled: it disappears and the student may start again
  const cancel = async () => {
    setSubmitting(true);
    try {
      await scholarshipsApi.post(`student/applications/${applicationId}/cancel`, null, { ifMatch: etag() });
      refreshLists();
      toast.success("Application cancelled");
      navigate("/my-applications");
    } catch (error) {
      console.error(`Failed to cancel application ${applicationId}:`, error);
      toast.error(errorMessageOf(error, "Failed to cancel. Please try again."));
      if (problemOf(error)?.code === "stale-version") reload();
    } finally {
      setSubmitting(false);
      setCancelling(false);
    }
  };

  // A sent application back to a draft, to change it before the deadline
  const reopen = async () => {
    setSubmitting(true);
    try {
      const result = await scholarshipsApi.post(`student/applications/${applicationId}/reopen`, null, { ifMatch: etag() });
      queryClient.setQueryData(myApplicationKey(applicationId), result);
      refreshLists();
      toast.success("You can change your application now. Submit it again before the deadline.");
    } catch (error) {
      console.error(`Failed to reopen application ${applicationId}:`, error);
      toast.error(errorMessageOf(error, "Failed to open your application for changes. Please try again."));
      reload();
    } finally {
      setSubmitting(false);
      setReopening(false);
    }
  };

  const withdraw = async () => {
    setSubmitting(true);
    try {
      const result = await scholarshipsApi.post(`student/applications/${applicationId}/withdraw`, withdrawReason.trim() ? { reason: withdrawReason.trim() } : {}, { ifMatch: etag() });
      queryClient.setQueryData(myApplicationKey(applicationId), result);
      refreshLists();
      setWithdrawing(false);
      toast.success("Application withdrawn");
    } catch (error) {
      console.error(`Failed to withdraw application ${applicationId}:`, error);
      toast.error(errorMessageOf(error, "Failed to withdraw. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  // Calls on an approved loan: the answer, and abroad the travel details and the visa
  const sendLoan = async (method, path, body, withVersion = true) => {
    try {
      const result = await scholarshipsApi[method](`student/applications/${applicationId}/${path}`, body, withVersion ? { ifMatch: etag() } : undefined);
      queryClient.setQueryData(myApplicationKey(applicationId), result);
      refreshLists();
    } catch (error) {
      if (problemOf(error)?.code === "stale-version") reload();
      throw error;
    }
  };

  const studentPath = `student/applications/${applicationId}`;
  const visaBody = (file) => {
    const body = new FormData();
    body.append("file", file);
    return body;
  };
  const loanActions = {
    respond: (body) => sendLoan("post", "loan/response", body),
    openLetter: () => openDocument(`${studentPath}/loan/letter/download-url`),
    save: (body) => sendLoan("put", "travel", body),
    submit: () => sendLoan("post", "travel/submit"),
    uploadVisa: (file) => sendLoan("post", "travel/visa", visaBody(file), false),
    openVisa: () => openDocument(`${studentPath}/travel/visa/download-url`),
    openTicket: () => openDocument(`${studentPath}/travel/ticket/download-url`),
  };

  // `problem` is the field's name in the API's errors (funding fields are `funding.<name>`)
  const field = (name, problem = name) => ({
    value: form[name],
    onChange: (e) => {
      setProblems((prev) => ({ ...prev, [problem]: undefined }));
      setForm((prev) => ({ ...prev, [name]: e.target.value }));
    },
    error: problems[problem],
    disabled: !editable || savingForm,
  });
  const fundingField = (name) => field(name, `funding.${name}`);
  // Cards are numbered in order; the funding card is for loans only
  const step = (n) => (loan ? n : n - 1);

  const general = [problems["deadline-passed"], problems.profile].filter(Boolean);

  return (
    <Container className="flex flex-col gap-6 py-6">
      <Link to="/my-applications" className="flex w-fit items-center gap-1 text-sm font-medium text-brand hover:underline">
        <ArrowLeftIcon className="h-4 w-4" /> My applications
      </Link>
      <PageHeader
        title={application.scholarship.name}
        subtitle={
          asksAdmission(kind)
            ? `${application.form.admittedProgramme || application.programme.name} · ${application.form.admittedInstitution || "Institution not given yet"}, ${
                application.form.studyCountryCode ? countryName(application.form.studyCountryCode) : "country not given yet"
              }`
            : `${application.programme.name} · ${application.programme.institutionName}, ${countryName(application.programme.countryCode)} · ${formatDuration(application.programme.durationMonths)}`
        }
        actions={<Tag color={status.color}>{status.label}</Tag>}
      />

      <div className={`rounded-lg p-4 text-sm ${status.color === "green" ? "bg-green-50 text-green-800" : "bg-slate-50 text-slate-700"}`}>
        <p>{status.hint}</p>
        {application.decisionReason && <p className="mt-1 italic">“{application.decisionReason}”</p>}
        {editable && (
          <p className="mt-1 font-medium text-amber-700">
            Deadline {formatKigaliDate(application.scholarship.closesAt)}, 23:59 Kigali time · {closesIn(application.scholarship.closesAt)}
          </p>
        )}
        {application.submittedAt && <p className="mt-1">Submitted {formatDateTime(application.submittedAt)}</p>}
      </div>

      <StudentDocumentRequests applicationId={applicationId} />

      {application.loan && <LoanCard applicationId={applicationId} loan={application.loan} travels={kind.travels} actions={loanActions} />}
      {application.loan?.response?.accepted && (
        <>
          {kind.travels && <TravelCard applicationId={applicationId} travel={application.loan.travel} editable={application.loan.travelEditable} actions={loanActions} />}
          <Card title="Messages with BRD">
            <MessageThread path={`student/applications/${applicationId}/messages`} side="STUDENT" />
          </Card>
        </>
      )}

      <Card title="1. About you">
        <SectionTitle title="Your studies" />
        <div className="mb-6 mt-3 grid gap-4 md:grid-cols-2">
          <Studies application={application} />
        </div>
        <SectionTitle title="About this application" />
        <div className="mt-3 grid gap-4 md:grid-cols-2">
          {asksAdmission(kind) && <AdmissionFields field={field} abroad={kind.studentChoosesPlace} />}
          {loan && <Select label="Priority sector of your studies" required placeholder="Choose a sector" options={COURSE_SECTORS} {...field("courseSector")} />}
          <EmploymentFields form={form} field={field} />
        </div>
      </Card>

      {loan && (
        <Card title="2. Funding you need">
          <FundingFields
            form={form}
            field={fundingField}
            started={{ value: form.started, onChange: (e) => setForm((prev) => ({ ...prev, started: e.target.value })), disabled: !editable || savingForm }}
            currencyOptions={currencyOptionsOf(options?.currencies, application.form.funding?.currency)}
            amounts={amounts}
          />
        </Card>
      )}

      <Card title={`${step(3)}. Documents`}>
        {!programme ? (
          <Loading label="Loading the documents…" />
        ) : (
          <ul className="flex flex-col divide-y divide-slate-100">
            {requirements.map((r) => (
              <RequirementRow
                key={r.id}
                requirement={r}
                document={application.documents.find((d) => d.requirementId === r.id)}
                editable={editable}
                busy={uploading === r.id}
                error={problems[`requirement:${r.id}`]}
                onUpload={upload}
                onDelete={removeDocument}
                onOpen={(d) => openDocument(`student/applications/${applicationId}/documents/${d.id}/download-url`, d.fileName)}
              />
            ))}
          </ul>
        )}
      </Card>

      {editable && consent && (
        <DeclarationCard
          title={`${step(4)}. Declaration and submit`}
          consent={consent}
          agreed={agreed}
          onAgree={setAgreed}
          error={problems.consentVersion}
          messages={general}
          onSubmit={preview}
          submitting={savingForm}
          submitLabel="Preview and submit"
          submitIcon={<EyeIcon className="h-4 w-4" />}
        />
      )}

      {/* Unsent: cancel it; sent: change it until the deadline, or withdraw it until the outcome */}
      {(editable || application.reopenable || application.withdrawable) && (
        <div className="flex flex-wrap justify-end gap-3">
          {editable && (
            <Button variant="danger-soft" onClick={() => setCancelling(true)}>
              Cancel application
            </Button>
          )}
          {/* One save for the whole form; previewing saves it too */}
          {editable && (
            <Button variant="outline" onClick={() => saveForm()} loading={savingForm} disabled={!formChanged} icon={<CheckIcon className="h-4 w-4" />}>
              Save draft
            </Button>
          )}
          {application.withdrawable && (
            <Button variant="danger-soft" onClick={() => setWithdrawing(true)}>
              Withdraw application
            </Button>
          )}
          {application.reopenable && (
            <Button variant="outline" icon={<PencilSquareIcon className="h-4 w-4" />} onClick={() => setReopening(true)}>
              Edit application
            </Button>
          )}
        </div>
      )}

      {previewing && (
        <ApplicationPreview
          application={application}
          kind={kind}
          form={form}
          requirements={requirements}
          amounts={amounts}
          submitting={submitting}
          onBack={() => setPreviewing(false)}
          onSubmit={submit}
        />
      )}
      <ConfirmModal
        open={cancelling}
        onClose={() => setCancelling(false)}
        onConfirm={cancel}
        loading={submitting}
        destructive
        title="Cancel your application?"
        description={`It will be removed from your applications, with the documents you uploaded. While the ${kind.one} is open you can start a new one.`}
        confirmLabel="Cancel application"
        cancelLabel="Keep it"
      />
      <ConfirmModal
        open={reopening}
        onClose={() => setReopening(false)}
        onConfirm={reopen}
        loading={submitting}
        title="Change your application?"
        description={`It goes back to a draft so you can change it. Submit it again before ${formatKigaliDate(application.scholarship.closesAt)}, 23:59 Kigali time, or it will not be considered.`}
        confirmLabel="Change it"
      />

      <ConfirmModal
        open={withdrawing}
        onClose={() => setWithdrawing(false)}
        onConfirm={withdraw}
        loading={submitting}
        destructive
        title="Withdraw your application?"
        description={`HEC will no longer consider it. While the ${kind.one} is still open you can start a new one.`}
        confirmLabel="Withdraw"
      >
        <Textarea label="Reason (optional)" rows={2} maxLength={500} value={withdrawReason} onChange={(e) => setWithdrawReason(e.target.value)} />
      </ConfirmModal>
    </Container>
  );
}
